import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Loader2, Check, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/* /invites/:token — co-branded invite landing. Shows seller branding big,
   verqo small. If logged-in and buyer → accept button. If logged-out →
   sign-in prompt. If no account → register CTA (email prefilled). */
export default function AcceptInvite() {
  const { token } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invites/preview/${token}`);
        setPreview(data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Invite not found");
      }
    })();
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/invites/accept/${token}`);
      toast.success(`Connected to ${data.tenant_name}`);
      localStorage.setItem("verqo_active_supplier_slug", data.tenant_slug);
      nav(`/buyer/catalog`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept");
    } finally { setAccepting(false); }
  };

  if (err) return (
    <div className="min-h-screen grid place-items-center p-8">
      <div className="max-w-md text-center">
        <h1 className="display text-3xl">Invite unavailable</h1>
        <p className="text-foreground/60 mt-3">{err}</p>
        <Link to="/" className="inline-block mt-6 text-foreground underline">Back home</Link>
      </div>
    </div>
  );
  if (!preview) return (
    <div className="min-h-screen grid place-items-center">
      <Loader2 className="w-6 h-6 animate-spin text-foreground/40"/>
    </div>
  );

  const t = preview.tenant || {};
  const branding = t.branding || {};
  const invite = preview.invite;
  const emailMismatch = user && invite.email && user.email.toLowerCase() !== invite.email.toLowerCase();

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* LEFT — seller-branded panel */}
      <div
        className="p-10 lg:p-16 flex flex-col justify-between"
        style={{ background: branding.primary_color || "#0a1f1c", color: "#fff" }}
      >
        <div className="flex items-center gap-2.5">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt={t.name} className="h-8 w-auto"/>
          ) : (
            <span
              className="w-8 h-8 rounded-md grid place-items-center font-bold display"
              style={{ background: branding.accent_color || "#e8ff3a", color: branding.primary_color || "#0a1f1c" }}
            >
              {(t.name || "?").slice(0,1)}
            </span>
          )}
          <span className="display font-semibold">{t.name}</span>
        </div>
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.22em]" style={{ color: branding.accent_color || "#e8ff3a" }}>
            You're invited
          </div>
          <h1 className="display text-4xl sm:text-5xl tracking-tight leading-[1.05] mt-3">
            {t.name} wants you<br/>on their wholesale.
          </h1>
          <div className="mt-6 space-y-2 text-[14.5px] text-white/85">
            <div className="flex items-center gap-2.5"><Check className="w-4 h-4" style={{ color: branding.accent_color || "#e8ff3a" }}/>Terms: <strong>{invite.default_terms.replace("NET_", "Net-")}</strong></div>
            {invite.additional_credit_limit > 0 && (
              <div className="flex items-center gap-2.5"><Check className="w-4 h-4" style={{ color: branding.accent_color || "#e8ff3a" }}/>Credit top-up: <strong>${invite.additional_credit_limit.toLocaleString()}</strong> on top of your verqo baseline</div>
            )}
            <div className="flex items-center gap-2.5"><Check className="w-4 h-4" style={{ color: branding.accent_color || "#e8ff3a" }}/>Private linesheet. Invite-only.</div>
          </div>
          {invite.note && (
            <div className="mt-6 rounded-xl bg-white/10 ring-1 ring-white/15 p-4 text-[14px] text-white/80">
              "{invite.note}"
            </div>
          )}
        </div>
        <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-white/40">
          Powered by verqo
        </div>
      </div>

      {/* RIGHT — action panel */}
      <div className="p-10 lg:p-16 flex flex-col justify-center">
        <div className="max-w-md">
          <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/55">Invitation details</div>
          <div className="display text-3xl tracking-tight mt-2">Accept & start buying</div>
          <p className="text-foreground/65 mt-3 text-[14.5px]">
            {user ? (
              <>You'll be connected to <strong>{t.name}</strong> and land on their storefront. Your credit line stays unified across every supplier on verqo.</>
            ) : (
              <>Sign in with your verqo account to accept. New here? Create a free account — it takes 60 seconds and works across every supplier who invites you.</>
            )}
          </p>

          <div className="mt-6 rounded-xl border border-foreground/10 bg-secondary/30 p-4 space-y-2 text-[13px]">
            <div className="flex items-center justify-between"><span className="text-foreground/55">Invited email</span><span className="font-medium">{invite.email}</span></div>
            <div className="flex items-center justify-between"><span className="text-foreground/55">Supplier</span><span className="font-medium">{invite.tenant_name}</span></div>
            <div className="flex items-center justify-between"><span className="text-foreground/55">Default terms</span><span className="font-medium">{invite.default_terms.replace("NET_", "Net-")}</span></div>
            {invite.additional_credit_limit > 0 && (
              <div className="flex items-center justify-between"><span className="text-foreground/55">Extra credit</span><span className="font-medium">+${invite.additional_credit_limit.toLocaleString()}</span></div>
            )}
          </div>

          {emailMismatch && (
            <div className="mt-4 text-[12px] text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5">
              You're signed in as {user.email} but this invite is for {invite.email}. You can still accept with your current account.
            </div>
          )}

          <div className="mt-7 flex items-center gap-3 flex-wrap">
            {user ? (
              <Button
                onClick={accept}
                disabled={accepting}
                data-testid="invite-accept"
                className="rounded-full h-11 px-6"
                style={{ background: branding.primary_color || "#0a1f1c", color: branding.accent_color || "#e8ff3a" }}
              >
                {accepting ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Accept invitation <ArrowRight className="w-4 h-4 ml-1"/></>}
              </Button>
            ) : (
              <>
                <Link to={`/login?as=buyer`} state={{ returnTo: `/invites/${token}` }}>
                  <Button className="rounded-full h-11 px-6 bg-foreground text-background">Sign in</Button>
                </Link>
                <Link to={`/register?as=buyer&email=${encodeURIComponent(invite.email)}&invite=${token}`} className="text-[13px] text-foreground/60 hover:text-foreground inline-flex items-center gap-1">
                  <Sparkles className="w-3.5 h-3.5"/>Create free buyer account
                </Link>
              </>
            )}
            {/* Read-only escape hatch — vet the seller before committing. */}
            {invite.tenant_slug && (
              <Link
                to={`/s/${invite.tenant_slug}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="invite-view-profile"
                className="rounded-full border border-foreground/20 hover:border-foreground bg-white px-4 h-11 inline-flex items-center gap-1.5 text-[13px]"
              >
                View seller profile
              </Link>
            )}
          </div>

          <p className="text-[11.5px] text-foreground/50 mt-6">
            verqo is the shared wholesale OS powering {t.name} and thousands of other brands. One login, every supplier who invites you, one credit line, one wallet.
          </p>
        </div>
      </div>
    </div>
  );
}
